import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ProjectDto,
  TextDocumentDto,
  useCreateProjectTextDocumentMutation,
  useListTextDocumentsForProjectQuery,
  useUpdateProjectMutation,
  ViewDocumentAsOwnerDto,
} from "../../../autogen/bff-api";
import { CombinedDocuments } from "../../../common/documents/CombinedDocuments";
import { IncompleteFileUpload, isIncompleteFileUpload } from "../../../common/documents/UppyUploader/FileRow";
import { useUppyUploader } from "../../../common/documents/UppyUploader/useUppyUploader";
import { useApiError } from "../../../common/errors/useApiError";

export const Documents = ({ project, refetch }: { project: ProjectDto; refetch: () => void }) => {
  const displayer = useApiError();
  const { t } = useTranslation();

  const [textDocuments, setTextDocuments] = useState<TextDocumentDto[]>([]);

  const [createTextDocument] = useCreateProjectTextDocumentMutation();
  const [updateProject] = useUpdateProjectMutation();

  const { data: textDocRes, isLoading: isLoadingTextDocuments } = useListTextDocumentsForProjectQuery({
    projectId: project.id,
  });

  const { uppy, allFiles, documents, setDocuments, setUploads, removeUpload } = useUppyUploader({
    entityId: project.id,
    entityType: "Project",
    reloadEntity: refetch,
  });

  const removeUploadedDocument = useCallback(
    async (document: ViewDocumentAsOwnerDto) => {
      await updateProject({
        updateProjectRequest: {
          id: project.id,
          removeDocument: document.id,
        },
      })
        .unwrap()
        .catch((e) => {
          displayer.trigger(e);
          throw e;
        });
    },
    [displayer, project.id, updateProject]
  );

  useEffect(() => {
    setUploads(project.uploads ?? []);
  }, [project.uploads, setUploads]);

  useEffect(() => {
    setDocuments(project.documents ?? []);
  }, [project.documents, setDocuments]);

  useEffect(() => {
    setTextDocuments(textDocRes?.documents ?? []);
  }, [textDocRes?.documents]);

  return (
    <CombinedDocuments
      uploadedDocuments={documents}
      textDocuments={textDocuments}
      canEditDocumentNames={project.canEdit}
      canDeleteDocuments={project.canEdit}
      canEditTextDocuments={project.canEdit}
      createTextDocument={() =>
        createTextDocument({
          createProjectTextDocumentRequest: {
            name: "",
            text: "",
            projectId: project.id,
            tags: [],
          },
        })
          .unwrap()
          .catch((e) => {
            displayer.show(e);
            throw e;
          })
      }
      documentUploadConfig={
        project.canEdit
          ? {
              uppy,
              dropTargetId: "projectFileTarget",
              dropTargetHelperText: t("Upload relevant project documents"),
              uploads: allFiles.filter((f) => isIncompleteFileUpload(f)) as IncompleteFileUpload[],
              removeUpload,
            }
          : undefined
      }
      isLoading={isLoadingTextDocuments}
      setUploadedDocuments={setDocuments}
      setTextDocuments={setTextDocuments}
      removeUploadedDocument={removeUploadedDocument}
      isTagsColumnVisible={true}
    />
  );
};
